var __importDefault = (this && this.__importDefault) || function (mod) {
  return (mod && mod.__esModule) ? mod : { "default": mod };
};
/**
 * CalendarPopup Component
 * Matches the original GRM yearly calendar day-popup: date header with event
 * count, event list with coloured left-border bars, and a full-width blue
 * "+ ADD AN EVENT" button at the bottom.
 */
import React, { memo, useRef, useEffect } from 'react';
import { generatePopupAriaLabel } from '../../utils/accessibilityUtils';
import { createFocusTrap } from '../../utils/accessibilityUtils';
import { format } from 'date-fns';
/* ------------------------------------------------------------------ */
/*  Styles                                                             */
/* ------------------------------------------------------------------ */
const s = {
    backdrop: {
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.25)', zIndex: 10000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
    },
    popup: {
        backgroundColor: '#fff', border: '1px solid #a19f9d',
        boxShadow: '0 4px 20px rgba(0,0,0,0.22)',
        minWidth: '340px', maxWidth: '480px', width: '90vw',
        maxHeight: '70vh', display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
    },
    header: {
        padding: '12px 16px', borderBottom: '1px solid #edebe9',
        fontSize: '14px', fontWeight: 600, color: '#323130',
    },
    content: { flex: 1, overflow: 'auto' },
    noEvents: {
        padding: '24px 16px', textAlign: 'center',
        color: '#605e5c', fontSize: '14px',
    },
    eventRow: {
        display: 'flex', alignItems: 'stretch', padding: '10px 16px',
        cursor: 'pointer', borderBottom: '1px solid #f3f2f1',
    },
    eventColorBar: {
        width: '4px', borderRadius: '2px', marginRight: '10px', flexShrink: 0,
    },
    eventBody: { flex: 1, minWidth: 0 },
    eventTitle: { fontSize: '13px', fontWeight: 600, color: '#323130', margin: 0 },
    eventTiming: { fontSize: '12px', color: '#605e5c', marginTop: '4px' },
    footer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '1px',
        backgroundColor: '#fff',
    },
    addBtn: {
        display: 'block', width: '100%', padding: '12px 0',
        backgroundColor: '#0063B1', color: '#fff', border: 'none',
        fontSize: '14px', fontWeight: 600, cursor: 'pointer',
        textAlign: 'center',
    },
};
/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */
const fmtDate = (dt, pattern) => {
    if (!dt)
        return '';
    try {
        const d = typeof dt === 'string' ? new Date(dt) : dt;
        if (isNaN(d.getTime()))
            return String(dt);
        return format(d, pattern);
    }
    catch (_a) {
        return String(dt);
    }
};
export const CalendarPopup = memo(({ isOpen, date, events, settings = {}, onClose, onEventClick, onLeftAction, }) => {
    const ref = useRef(null);
    useEffect(() => {
        if (!isOpen)
            return;
        // Honour the configured behaviour instead of always closing on Escape.
        if (settings.closeOnEscape === false)
            return;
        const h = (e) => { if (e.key === 'Escape')
            onClose === null || onClose === void 0 ? void 0 : onClose(); };
        document.addEventListener('keydown', h);
        return () => document.removeEventListener('keydown', h);
    }, [isOpen, onClose, settings.closeOnEscape]);
    useEffect(() => {
        if (isOpen && ref.current) {
            const el = ref.current.querySelector('[tabindex="0"], button');
            el === null || el === void 0 ? void 0 : el.focus();
            // Only trap focus when configured to do so.
            if (settings.trapFocus !== false) {
                return createFocusTrap(ref.current);
            }
        }
    }, [isOpen, settings.trapFocus]);
    if (!isOpen || !date)
        return null;
    // Respect showWhenEventsEmpty: suppress the popup for days with no events when
    // the setting is explicitly disabled.
    if (events.length === 0 && settings.showWhenEventsEmpty === false)
        return null;
    const ariaLabel = generatePopupAriaLabel(date, events.length);
    const dateStr = fmtDate(date, 'dd-MMM-yyyy').toUpperCase();
    const countLabel = events.length > 0
        ? ` (${events.length} EVENT${events.length > 1 ? 'S' : ''})`
        : '';
    return (React.createElement("div", { style: s.backdrop, onClick: (e) => { if (e.target === e.currentTarget && settings.closeOnOutsideClick !== false)
            onClose === null || onClose === void 0 ? void 0 : onClose(); }, role: "presentation" },
        React.createElement("div", { ref: ref, style: s.popup, role: "dialog", "aria-label": ariaLabel, "aria-modal": "true" },
            React.createElement("div", { style: s.header },
                dateStr,
                countLabel),
            React.createElement("div", { style: s.content }, events.length === 0 ? (React.createElement("div", { style: s.noEvents }, settings.noEventsLabel || 'No events scheduled')) : (events
                .slice()
                .sort((a, b) => (a.isPublicHoliday ? -1 : 1) - (b.isPublicHoliday ? -1 : 1))
                .map((evt) => {
                const isPublicHoliday = evt.isPublicHoliday;
                // Holiday rows use a light purple/pink background (secondary color)
                const holidayBg = isPublicHoliday ? '#B4A0FF' : '#fff';
                return (React.createElement("div", { key: evt.id, style: Object.assign(Object.assign({}, s.eventRow), { backgroundColor: holidayBg }), role: "button", tabIndex: 0, onClick: () => onEventClick === null || onEventClick === void 0 ? void 0 : onEventClick(evt), onKeyDown: (e) => { if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onEventClick === null || onEventClick === void 0 ? void 0 : onEventClick(evt);
                    } }, "aria-label": `${evt.name}${evt.subject ? ': ' + evt.subject : ''}, ${fmtDate(evt.startDt, 'dd-MMM-yyyy')}` },
                    React.createElement("div", { style: Object.assign(Object.assign({}, s.eventColorBar), { backgroundColor: evt.bgcolor || '#a9a9a9' }) }),
                    React.createElement("div", { style: s.eventBody },
                        React.createElement("p", { style: Object.assign(Object.assign({}, s.eventTitle), { color: isPublicHoliday ? '#323130' : '#323130' }) },
                            evt.name,
                            evt.subject && !isPublicHoliday ? ` : ${evt.subject}` : ''),
                        isPublicHoliday ? (React.createElement("div", { style: Object.assign(Object.assign({}, s.eventTiming), { color: '#323130' }) }, evt.holidayType || '')) : (React.createElement("div", { style: s.eventTiming },
                            fmtDate(evt.startDtFQ || evt.startDt, 'dd-MMM-yyyy hh:mm:ss a'),
                            '  -  ',
                            fmtDate(evt.endDtFQ || evt.endDt, 'dd-MMM-yyyy hh:mm:ss a'),
                            evt.timeMetaData && React.createElement(React.Fragment, null,
                                React.createElement("br", null),
                                evt.timeMetaData))))));
            }))),
            settings.showLeft !== false && (React.createElement("div", { style: s.footer },
                React.createElement("button", { style: s.addBtn, onClick: onLeftAction, "aria-label": `Add event on ${dateStr}` },
                    "+ ",
                    (settings.leftLabel || 'Add Event').toUpperCase()))))));
});
CalendarPopup.displayName = 'CalendarPopup';
export default CalendarPopup;
//# sourceMappingURL=CalendarPopup.js.map